import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { createAnswerQueue } from "@/lib/answerQueue.mjs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

function fmtClock(s) {
  if (s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
}

// ─── helper: find source text for a question ────────────────────────────────
function getSourceText(q, questions) {
  if (q.source_text) return { text: q.source_text, label: q.source_text_label };
  if (q.source_text_id) {
    const found = questions.find(
      (qq) => qq.source_text_id === q.source_text_id && qq.source_text
    );
    if (found) return { text: found.source_text, label: found.source_text_label };
  }
  return null;
}

// ─── helper: check if question has a non-empty answer ────────────────────────
function isAnswered(ans) {
  if (!ans) return false;
  const ua = ans.user_answer;
  if (ua === undefined || ua === null || ua === "") return false;
  if (Array.isArray(ua)) return ua.some((v) => v !== "" && v !== null && v !== undefined);
  if (typeof ua === "object") return Object.values(ua).some((v) => v !== "" && v !== null && v !== undefined);
  return true;
}

// ─── Source Text Panel ────────────────────────────────────────────────────────
function SourceTextPanel({ source }) {
  const [collapsed, setCollapsed] = useState(false);
  if (!source?.text) return null;

  const isLong = source.text.length > 500;

  return (
    <div className="mb-8 border border-stone bg-bone rounded-sm overflow-hidden shadow-[0_1px_4px_0_rgba(0,0,0,0.06)]">
      {/* Header row */}
      <div className="px-5 py-2.5 bg-parchment border-b border-stone flex items-center justify-between">
        <span className="text-[11px] font-bold tracking-[0.14em] uppercase text-ink select-none">
          {source.label || "VÝCHOZÍ TEXT"}
        </span>
        {isLong && (
          <button
            className="text-[11px] text-slate hover:text-ink transition-colors"
            onClick={() => setCollapsed((c) => !c)}
          >
            {collapsed ? "▼ zobrazit" : "▲ sbalit"}
          </button>
        )}
      </div>

      {/* Text body */}
      {!collapsed && (
        <div className={`px-6 py-5 ${isLong ? "max-h-80 overflow-y-auto" : ""}`}>
          <div className="font-serif text-ink text-[15px] leading-[1.8] whitespace-pre-wrap">
            {source.text}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Question type: true_false_multi ─────────────────────────────────────────
function TrueFalseMulti({ q, value, onChange }) {
  const subs = q.subquestions || [];
  const arr = Array.isArray(value) ? value : new Array(subs.length).fill(null);

  const set = (i, v) => {
    const next = [...arr];
    next[i] = v;
    onChange(next);
  };

  return (
    <div className="space-y-0 border border-stone rounded-sm overflow-hidden" data-testid="answer-tfm">
      <div className="grid grid-cols-[1fr_auto_auto] bg-stone/30 border-b border-stone">
        <span className="px-4 py-2 caption">Tvrzení</span>
        <span className="px-4 py-2 caption text-center w-16">A</span>
        <span className="px-4 py-2 caption text-center w-16">N</span>
      </div>
      {subs.map((sub, i) => (
        <div
          key={sub.label}
          className={`grid grid-cols-[1fr_auto_auto] border-b border-stone last:border-b-0 ${
            i % 2 === 0 ? "bg-parchment" : "bg-bone"
          }`}
        >
          <div className="px-4 py-3 text-sm text-ink leading-relaxed">
            <span className="font-mono text-slate mr-2">{sub.label}</span>
            {sub.text}
          </div>
          {["A", "N"].map((opt) => (
            <button
              key={opt}
              onClick={() => set(i, opt)}
              className={`w-16 flex items-center justify-center border-l border-stone transition-colors ${
                arr[i] === opt
                  ? "bg-navy text-parchment font-bold"
                  : "text-slate hover:bg-stone/30"
              }`}
              data-testid={`tfm-${i}-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── Question type: matching ──────────────────────────────────────────────────
function Matching({ q, value, onChange }) {
  const items = q.matching_items || [];
  const choices = q.matching_choices || [];
  const dict = typeof value === "object" && !Array.isArray(value) && value ? value : {};

  const set = (label, choice) => {
    onChange({ ...dict, [label]: choice });
  };

  return (
    <div className="space-y-3" data-testid="answer-matching">
      {items.map((item) => (
        <div key={item.label} className="flex items-start gap-4 border border-stone rounded-sm p-3 bg-bone">
          <div className="flex-1 text-sm text-ink leading-relaxed">
            <span className="font-mono text-slate mr-2">{item.label}</span>
            {item.text}
          </div>
          <Select
            value={dict[item.label] || ""}
            onValueChange={(v) => set(item.label, v)}
          >
            <SelectTrigger className="w-[80px] border-stone bg-parchment text-sm shrink-0" data-testid={`match-${item.label}`}>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {choices.map((ch) => (
                <SelectItem key={ch.label} value={ch.label}>
                  {ch.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
      {/* Choice legend */}
      <div className="mt-4 border border-stone rounded-sm p-3 bg-bone/50 space-y-1">
        {choices.map((ch) => (
          <p key={ch.label} className="text-sm text-ink">
            <span className="font-mono font-bold text-navy mr-2">{ch.label}</span>
            {ch.text}
          </p>
        ))}
      </div>
    </div>
  );
}

// ─── Question type: ordering ──────────────────────────────────────────────────
function Ordering({ q, value, onChange }) {
  const parts = q.ordering_parts || [];
  const labels = parts.map((p) => p.label);
  const arr = Array.isArray(value) ? value : new Array(parts.length).fill("");

  const set = (slotIdx, label) => {
    const next = [...arr];
    next[slotIdx] = label;
    onChange(next);
  };

  return (
    <div className="space-y-6" data-testid="answer-ordering">
      {/* Slot selectors */}
      <div className="space-y-2">
        <p className="caption text-slate mb-2">Určete pořadí (1. = první):</p>
        {parts.map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="font-mono text-slate w-6 text-right">{i + 1}.</span>
            <Select value={arr[i] || ""} onValueChange={(v) => set(i, v)}>
              <SelectTrigger className="w-[80px] border-stone bg-parchment" data-testid={`ord-slot-${i}`}>
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {labels.map((lbl) => (
                  <SelectItem key={lbl} value={lbl}>
                    {lbl}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      {/* Fragments to read */}
      <div className="space-y-2 border-t border-stone pt-4">
        <p className="caption text-slate mb-2">Úryvky k seřazení:</p>
        {parts.map((part) => (
          <div key={part.label} className="flex gap-3 border border-stone rounded-sm p-3 bg-bone text-sm">
            <span className="font-mono font-bold text-navy shrink-0">{part.label}</span>
            <span className="text-ink leading-relaxed">{part.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Question type: open_multi ────────────────────────────────────────────────
function OpenMulti({ q, value, onChange }) {
  const slots = q.answer_slots || 3;
  const labels = q.answer_labels || null;
  const arr = Array.isArray(value) ? value : new Array(slots).fill("");

  const set = (i, v) => {
    const next = [...arr];
    next[i] = v;
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid="answer-open-multi">
      {Array.from({ length: slots }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          {labels && <span className="caption w-10 text-right shrink-0">{labels[i] || `${i + 1}.`}</span>}
          <Input
            value={arr[i] || ""}
            onChange={(e) => set(i, e.target.value)}
            className="bg-bone border-stone focus-visible:ring-navy"
            placeholder={`Odpověď ${labels ? labels[i] : i + 1}`}
            data-testid={`om-input-${i}`}
          />
        </div>
      ))}
    </div>
  );
}

// ─── Question type: open_pair ─────────────────────────────────────────────────
function OpenPair({ q, value, onChange }) {
  const labels = q.answer_labels || ["1", "2"];
  const dict = typeof value === "object" && !Array.isArray(value) && value ? value : {};

  const set = (label, v) => {
    onChange({ ...dict, [label]: v });
  };

  return (
    <div className="space-y-3" data-testid="answer-open-pair">
      {labels.map((label) => (
        <div key={label} className="flex items-center gap-3">
          <span className="caption w-10 text-right shrink-0">{label}</span>
          <Input
            value={dict[label] || ""}
            onChange={(e) => set(label, e.target.value)}
            className="bg-bone border-stone focus-visible:ring-navy"
            placeholder={`Doplňte ${label}`}
            data-testid={`op-input-${label}`}
          />
        </div>
      ))}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function TestRunner() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [bundle, setBundle] = useState(null);
  const [answers, setAnswers] = useState({});
  const [current, setCurrent] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState("saved");
  const answersRef = useRef({});
  const submitLock = useRef(false);
  const submitRef = useRef(null);
  const deadlineRef = useRef(0);
  const queue = useMemo(() => createAnswerQueue((qid, rec) => api.post(`/attempts/${attemptId}/answer`, {
    question_id: qid, user_answer: rec.user_answer ?? null,
    time_spent_seconds: rec.time_spent_seconds || 0, marked_for_review: !!rec.marked,
  }), setSaveStatus), [attemptId]);

  useEffect(() => {
    api.get(`/attempts/${attemptId}`).then((r) => {
      const restored = Object.fromEntries(Object.entries(r.data.attempt.answers || {}).map(([id, rec]) => [id, {...rec, marked: !!rec.marked_for_review}]));
      answersRef.current = restored;
      setAnswers(restored);
      setBundle(r.data);
      const limitS = (r.data.test?.time_limit_minutes || 70) * 60;
      const serverNow = new Date(r.data.server_time).getTime();
      const end = r.data.attempt.deadline_at ? new Date(r.data.attempt.deadline_at).getTime() : new Date(r.data.attempt.started_at).getTime() + limitS * 1000;
      deadlineRef.current = Date.now() + Math.max(0, end - serverNow);
      setRemaining(Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000)));
    }).catch(() => {
      toast.error("Pokus se nepodařilo načíst");
      navigate("/testy");
    });
  }, [attemptId, navigate]);

  useEffect(() => {
    if (!bundle || bundle.attempt.status !== "in_progress") return;
    const tick = () => {
      const left = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000));
      setRemaining(left);
      if (left === 0) { clearInterval(timer); submitRef.current?.(true); }
    };
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [bundle]);

  useEffect(() => {
    const guard = (event) => {
      if (queue.hasPending()) { event.preventDefault(); event.returnValue = ""; }
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [queue]);

  const questions = bundle?.questions || [];
  const q = questions[current];
  const total = questions.length;

  const saveLocal = (qid, record) => {
    if (submitLock.current || Date.now() >= deadlineRef.current) return;
    const next = {...answersRef.current, [qid]: record};
    answersRef.current = next;
    setAnswers(next);
    queue.enqueue(qid, record).catch(() => {}); // queue exposes visible error and retains the change
  };
  const setAnswerLocal = (qid, value) => saveLocal(qid, {...(answersRef.current[qid] || {}), user_answer: value});
  const toggleReview = (qid) => {
    const current = answersRef.current[qid] || {};
    saveLocal(qid, {...current, marked: !current.marked});
  };

  const answeredCount = useMemo(
    () => Object.values(answers).filter((a) => isAnswered(a)).length,
    [answers]
  );

  const handleSubmit = async (auto = false) => {
    if (submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    try {
      try {
        await queue.flush();
      } catch (error) {
        if (!auto || error.response?.status !== 409) throw error;
        toast.error("Čas vypršel. Některé změny nebyly uloženy; započítají se pouze odpovědi potvrzené serverem.");
      }
      await api.post(`/attempts/${attemptId}/submit`);
      toast.success(auto ? "Čas vypršel — test byl automaticky odevzdán." : "Test odevzdán.");
      navigate(`/vysledek/${attemptId}`);
    } catch (e) {
      toast.error("Test se nepodařilo uložit a odevzdat. Zůstaň na stránce a zkus to znovu.");
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  submitRef.current = handleSubmit;

  if (!bundle) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-parchment caption" data-testid="runner-loading">
        Načítání testu…
      </div>
    );
  }
  if (bundle.attempt.status === "completed") {
    navigate(`/vysledek/${attemptId}`);
    return null;
  }

  const source = q ? getSourceText(q, questions) : null;
  const timerColor =
    remaining < 5 * 60 ? "text-burgundy" : remaining < 10 * 60 ? "text-goldenrod" : "text-ink";

  return (
    <div className="min-h-screen bg-parchment flex flex-col" data-testid="test-runner-root">
      {/* Top bar */}
      <div className="border-b border-stone bg-parchment/90 backdrop-blur sticky top-0 z-20">
        <div className="max-w-[900px] mx-auto px-6 lg:px-8 py-4 flex items-center justify-between gap-6">
          <div>
            <p className="caption mb-1">Otázka {current + 1} / {total}</p>
            <p className="font-serif text-base text-ink truncate max-w-[40ch]">{bundle.test?.title}</p>
          </div>
          <div className="text-right">
            <p className="caption mb-1">Zbývá</p>
            <p className={`font-mono tabular text-3xl ${timerColor}`} data-testid="timer">
              {fmtClock(remaining)}
            </p>
          </div>
        </div>
        <Progress value={((current + 1) / total) * 100} className="rounded-none h-[2px] bg-stone" />
      </div>

      {/* Question */}
      <div className="flex-1 px-6 py-10">
        <div className="max-w-[780px] mx-auto">
          <p className="caption mb-4">
            Téma: <span className="text-ink">{q?.topic_tag?.replaceAll("_", " ")}</span>
            {" · "}
            {q?.points} {q?.points === 1 ? "bod" : q?.points <= 4 ? "body" : "bodů"}
          </p>

          <div role="status" className="mb-6 text-sm text-slate">
            {saveStatus === "saved" ? "Odpovědi uloženy" : saveStatus === "saving" ? "Ukládám odpovědi…" : "Změny nejsou uložené. Zůstaň na stránce."}
            {saveStatus === "error" && <button className="ml-3 underline" onClick={() => queue.flush().catch(() => {})}>Zkusit uložit znovu</button>}
          </div>
          {/* Source text panel */}
          <SourceTextPanel source={source} />

          {/* Question text */}
          <h2
            className="font-serif text-xl lg:text-2xl text-ink leading-[1.4] mb-3"
            data-testid="question-text"
          >
            {q?.question_text}
          </h2>

          {/* Instruction */}
          {q?.instruction && (
            <p className="text-sm text-slate italic mb-8">({q.instruction})</p>
          )}

          {!q?.instruction && <div className="mb-8" />}

          {q?.image_url && (
            <img src={q.image_url} alt="" className="mb-8 border border-stone rounded-sm" />
          )}

          {/* ── multiple_choice ── */}
          {q?.question_type === "multiple_choice" && (
            <RadioGroup
              value={answers[q.id]?.user_answer || ""}
              onValueChange={(v) => setAnswerLocal(q.id, v)}
              className="space-y-4"
              data-testid="answer-mc"
            >
              {q.options?.map((opt, i) => {
                const letter = String.fromCharCode(65 + i); // A, B, C, D
                const selected = answers[q.id]?.user_answer === letter;
                return (
                  <Label
                    key={i}
                    htmlFor={`opt-${q.id}-${i}`}
                    className={`flex items-start gap-4 cursor-pointer py-3 pl-4 transition-colors ${
                      selected
                        ? "border-l-[3px] border-navy text-navy"
                        : "border-l-[3px] border-transparent text-ink hover:border-stone"
                    }`}
                    data-testid={`mc-option-${i}`}
                  >
                    <RadioGroupItem id={`opt-${q.id}-${i}`} value={letter} className="mt-1" />
                    <span className="text-base leading-relaxed">
                      <span className="font-mono font-bold mr-2">{letter}</span>
                      {opt}
                    </span>
                  </Label>
                );
              })}
            </RadioGroup>
          )}

          {/* ── true_false ── */}
          {q?.question_type === "true_false" && (
            <RadioGroup
              value={
                answers[q.id]?.user_answer === undefined || answers[q.id]?.user_answer === null
                  ? ""
                  : String(answers[q.id]?.user_answer)
              }
              onValueChange={(v) => setAnswerLocal(q.id, v === "true")}
              className="space-y-4"
              data-testid="answer-tf"
            >
              {[{ v: "true", l: "Pravda" }, { v: "false", l: "Nepravda" }].map(({ v, l }) => {
                const cur = answers[q.id]?.user_answer;
                const selected = cur !== undefined && cur !== null && String(cur) === v;
                return (
                  <Label
                    key={v}
                    htmlFor={`tf-${v}`}
                    className={`flex items-start gap-4 cursor-pointer py-3 pl-4 ${
                      selected
                        ? "border-l-[3px] border-navy text-navy"
                        : "border-l-[3px] border-transparent text-ink hover:border-stone"
                    }`}
                    data-testid={`tf-option-${v}`}
                  >
                    <RadioGroupItem id={`tf-${v}`} value={v} className="mt-1" />
                    <span className="text-base">{l}</span>
                  </Label>
                );
              })}
            </RadioGroup>
          )}

          {/* ── numeric / open_answer ── */}
          {(q?.question_type === "numeric" || q?.question_type === "open_answer") && (
            <div className="space-y-2">
              <Label className="caption">Tvá odpověď</Label>
              <Input
                type="text"
                value={answers[q.id]?.user_answer ?? ""}
                onChange={(e) => setAnswerLocal(q.id, e.target.value)}
                className="bg-bone border-stone focus-visible:ring-navy text-lg py-6"
                data-testid="answer-text-input"
                inputMode={q.question_type === "numeric" ? "decimal" : "text"}
              />
              {q.question_type === "numeric" && (
                <p className="text-xs text-slate">
                  Tip: desetinné číslo zapiš s tečkou i čárkou — obojí přijmeme.
                </p>
              )}
            </div>
          )}

          {/* ── open_short ── */}
          {q?.question_type === "open_short" && (
            <div className="space-y-2">
              <Label className="caption">Tvá odpověď</Label>
              <Input
                type="text"
                value={answers[q.id]?.user_answer ?? ""}
                onChange={(e) => setAnswerLocal(q.id, e.target.value)}
                className="bg-bone border-stone focus-visible:ring-navy text-lg py-6"
                data-testid="answer-open-short"
              />
            </div>
          )}

          {/* ── true_false_multi ── */}
          {q?.question_type === "true_false_multi" && (
            <TrueFalseMulti
              q={q}
              value={answers[q.id]?.user_answer}
              onChange={(v) => setAnswerLocal(q.id, v)}
            />
          )}

          {/* ── matching ── */}
          {q?.question_type === "matching" && (
            <Matching
              q={q}
              value={answers[q.id]?.user_answer}
              onChange={(v) => setAnswerLocal(q.id, v)}
            />
          )}

          {/* ── ordering ── */}
          {q?.question_type === "ordering" && (
            <Ordering
              q={q}
              value={answers[q.id]?.user_answer}
              onChange={(v) => setAnswerLocal(q.id, v)}
            />
          )}

          {/* ── open_multi ── */}
          {q?.question_type === "open_multi" && (
            <OpenMulti
              q={q}
              value={answers[q.id]?.user_answer}
              onChange={(v) => setAnswerLocal(q.id, v)}
            />
          )}

          {/* ── open_pair ── */}
          {q?.question_type === "open_pair" && (
            <OpenPair
              q={q}
              value={answers[q.id]?.user_answer}
              onChange={(v) => setAnswerLocal(q.id, v)}
            />
          )}

          <div className="mt-10">
            <button
              onClick={() => toggleReview(q.id)}
              className={`text-sm underline underline-offset-4 ${
                answers[q.id]?.marked ? "text-burgundy" : "text-slate"
              }`}
              data-testid="mark-review-btn"
            >
              {answers[q.id]?.marked ? "Zrušit označení k revizi" : "Označit k revizi"}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-stone bg-parchment">
        <div className="max-w-[900px] mx-auto px-6 lg:px-8 py-4 flex items-center justify-between gap-6">
          <Button
            variant="outline"
            disabled={current === 0}
            onClick={() => setCurrent((c) => Math.max(0, c - 1))}
            className="border-stone text-ink hover:bg-lemon rounded-sm"
            data-testid="prev-btn"
          >
            Předchozí
          </Button>

          {/* Question pad */}
          <div className="hidden md:flex flex-wrap gap-1 max-w-[480px]">
            {questions.map((qq, i) => {
              const ans = answers[qq.id];
              const filled = isAnswered(ans);
              return (
                <button
                  key={qq.id}
                  onClick={() => setCurrent(i)}
                  data-testid={`q-jump-${i}`}
                  className={`w-8 h-8 text-xs font-mono tabular border ${
                    i === current
                      ? "border-navy bg-navy text-parchment"
                      : ans?.marked
                      ? "border-burgundy text-burgundy"
                      : filled
                      ? "border-navy text-navy"
                      : "border-stone text-slate"
                  }`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>

          {current < total - 1 ? (
            <Button
              onClick={() => setCurrent((c) => Math.min(total - 1, c + 1))}
              className="bg-navy hover:bg-navy-dark text-parchment rounded-sm"
              data-testid="next-btn"
            >
              Další
            </Button>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  className="bg-navy hover:bg-navy-dark text-parchment rounded-sm"
                  data-testid="submit-btn"
                >
                  Odevzdat test
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-bone">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-serif text-2xl">Odevzdat test?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Zodpověděl(a) jsi {answeredCount} z {total} otázek. Po odevzdání už nelze nic měnit a uvidíš vyhodnocení.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-sm" data-testid="confirm-cancel">
                    Zatím ne
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-navy hover:bg-navy-dark text-parchment rounded-sm"
                    onClick={() => handleSubmit(false)}
                    data-testid="confirm-submit"
                  >
                    Odevzdat
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
        <div className="max-w-[900px] mx-auto px-6 lg:px-8 pb-4 flex items-center justify-between">
          <Link
            to="/testy"
            className="text-xs text-slate underline underline-offset-4"
            data-testid="abandon-btn"
          >
            Pozastavit a vrátit se později
          </Link>
          <p className="caption">{answeredCount}/{total} vyplněno</p>
        </div>
      </div>
    </div>
  );
}
